<template>
  <div>
    <v-divider />

    <v-row
      dense
      no-gutters
    >
      <v-col
        @click="viewSelectedResults = false"
        @keydown.enter="viewSelectedResults = false"
        :class="!viewSelectedResults ? 'elevation-2' : 'elevation-0'"
        class="pointer"
      >
        <div
          :class="!viewSelectedResults ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-4 d-flex align-center justify-center"
          data-cy="search-mode-button"
          data-testid="search-mode-button"
          role="button"
        >
          {{ $bct('Search') }}
        </div>
      </v-col>
      <v-col
        @click="viewSelectedResults = true"
        @keydown.enter="viewSelectedResults = true"
        :class="viewSelectedResults ? 'elevation-2' : 'elevation-0'"
        class="pointer"
        cols="6"
      >
        <div
          :class="viewSelectedResults ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-4 d-flex align-center justify-center"
          data-cy="selected-mode-button"
          data-testid="selected-mode-button"
          role="button"
        >
          {{ $bct('Selected') }} ({{ providerIds.length }})
        </div>
      </v-col>
    </v-row>

    <v-divider />

    <div class="bg-super-light-blue px-4">
      <div
        v-if="viewSelectedResults"
        class="py-3"
      >
        <div
          v-for="providerId in providerIds"
          :key="providerId"
          class="mb-6"
        >
          <SimpleProviderCard
            @open="emit('open', providerId)"
            @toggle="emit('toggle', [providerId, $event])"
            :active="providerIds.includes(providerId)"
            :provider-id="providerId"
            :selectable="belowSelectionLimit"
            class="my-4"
          />
        </div>
      </div>

      <div
        v-show="!viewSelectedResults"
        class="py-2"
      >
        <v-tabs
          v-model="searchTab"
          @update:model-value="resetResults"
          bg-color="transparent"
          class="mb-2"
        >
          <v-tab>
            <span
              class="ls-normal tt-none fs-16 fw-500"
              data-cy="address-mode-button"
            >
              {{ $bct('Search by address') }}
            </span>
          </v-tab>
          <v-tab>
            <span
              class="ls-normal tt-none fs-16 fw-500"
              data-cy="name-mode-button"
            >
              {{ $bct('Search by name') }}
            </span>
          </v-tab>
        </v-tabs>

        <v-card
          flat
          tile
        >
          <v-row no-gutters>
            <v-col class="d-flex flex-grow-1 flex-shrink-0">
              <v-text-field
                v-model="searchQuery"
                @keydown.enter="searchProviders"
                @update:model-value="searchQueryChanged = true"
                id="search_input"
                ref="search"
                :aria-label="searchTab == 0 ? $bct('Search by address') : $bct('Search by name')"
                class="b-radius-0 elevation-0"
                data-cy="query-input"
                prepend-inner-icon="search"
                variant="solo"
                autofocus
                hide-details
                tile
              />
            </v-col>
            <v-col class="d-flex justify-end flex-grow-0 flex-shrink-0">
              <v-btn
                @click="searchProviders"
                :disabled="!searchQueryChanged"
                :height="58"
                :loading="searching"
                :ripple="false"
                class="focus-very-visible"
                color="primary"
                data-cy="search-button"
                data-testid="search-button"
                size="x-large"
                tile
              >
                {{ $bct('Search') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <template v-if="subsidyProgram.selection_limit > 0">
          <v-row class="mb-3 mt-0">
            <v-col>
              <span
                class="fs-16 fw-800 c-primary"
                data-testid="selection-text"
              >
                {{ $bct(`You can select ${subsidyProgram.selection_limit} total options`) }}
              </span>
            </v-col>
          </v-row>
        </template>

        <template v-if="!providerIds.length > 0">
          <template v-if="previousSubsidyProviderIds">
            <v-btn
              @click="copyPrevious"
              color="primary"
              prepend-icon="redo"
            >
              {{ $bct('Copy selections from') }}: {{ previousChildName }}
            </v-btn>
          </template>
        </template>

        <template v-if="matches.length == 0">
          <p
            class="fs-16 mt-2"
            data-cy="results-hint"
          >
            <span class="me-1">
              {{ $bct('Search by your address to see results near you.') }}
            </span>
          </p>
        </template>

        <template v-if="searched && searchTab == 0">
          <v-row class="d-flex align-center mb-1 mt-2">
            <v-col
              cols="12"
              sm="8"
            >
              <template
                v-if="
                  store.state.schemas[subsidyProgram.provider_schema_id].meta.openings.enabled &&
                  store.state.schemas[subsidyProgram.provider_schema_id].meta.openings.public
                "
              >
                <FilterMenu
                  @clear="filters.vacancy = null"
                  @close="save()"
                  :actions="true"
                  :active="filters.vacancy"
                  :active-classes="['bg-white', 'bc-primary', 'b-1']"
                  :inactive-classes="['bg-white']"
                  :outlined="false"
                  data-cy="vacancy-filter"
                  elevation="2"
                  tid="search_availability_filter"
                  title="Availability"
                  hide-clear-filter
                >
                  <template #card>
                    <v-checkbox
                      v-model="filters.vacancy"
                      class="mt-0 mb-0"
                      color="primary"
                      false-icon="check_box_outline_blank"
                      true-icon="check_box"
                      hide-details
                    >
                      <template #label>
                        <span
                          class="c-black"
                          data-cy="availability-checkmark"
                        >
                          {{ $bct('Only show providers that have reported availability') }}
                        </span>
                      </template>
                    </v-checkbox>
                  </template>
                </FilterMenu>
              </template>

              <template v-if="!searchFlags.dobSearchEnabled && !searchFlags.defaultAgeGroupEnabled">
                <FilterMenu
                  @close="save()"
                  :actions="true"
                  :active="true"
                  :active-classes="['bg-white', 'bc-primary', 'b-1']"
                  :inactive-classes="['bg-white']"
                  :outlined="false"
                  data-cy="age-filter"
                  elevation="2"
                  tid="search_ages_filter"
                  title="Age"
                  hide-clear-filter
                >
                  <template #card>
                    <LabeledVolume
                      v-for="(item, index) in ageGroups"
                      @decrease="modifyCount(index, -1)"
                      @increase="modifyCount(index, 1)"
                      :key="index"
                      ref="childVolume"
                      :count="item.count"
                      :dense="true"
                      :subtitle="item.subtitle || item.subtext"
                      :title="item.title || item.text"
                    />
                  </template>
                </FilterMenu>
              </template>

              <v-btn
                v-if="searchFlags.moreFiltersEnabled"
                @click="moreFiltersDialogIsVisible = true"
                :class="moreFiltersButtonClass"
                class="focus-visible"
                data-cy="more-filters-button"
                data-tid="search_more_filter"
                elevation="2"
                rounded
              >
                <span
                  class="bc-body c-black d-none d-md-flex fw-500 fs-14"
                  data-cy="more-filters"
                >
                  {{ $bct('More filters') }}
                </span>
                <v-icon
                  class="d-flex d-md-none fw-500"
                  icon="more_horiz"
                />
              </v-btn>

              <v-dialog
                v-model="moreFiltersDialogIsVisible"
                width="600px"
                scrollable
              >
                <v-card>
                  <v-card-title class="mb-n-3 pb-0">
                    {{ $bct('More filters') }}
                  </v-card-title>
                  <v-card-text class="pt-0 pb-6">
                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_types.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_types.alias || 'Program types',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-checkbox
                            v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                              .definition.properties.program_types.items.enum"
                            v-model="filters.program_types"
                            :key="type"
                            :label="capitalize($bct(type))"
                            :value="type"
                            class="c-black mt-0"
                            color="primary"
                            hide-details
                            multiple
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .categories.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.alias || 'Categories',
                            )
                          }}
                        </v-col>
                      </v-row>

                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <div
                            v-if="
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.items.enum.length < 20
                            "
                          >
                            <v-checkbox
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.categories.items.enum"
                              v-model="filters.categories"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                              multiple
                            />
                          </div>
                          <div v-else>
                            <v-autocomplete
                              v-model="filters.categories"
                              :items="
                                store.state.schemas[subsidyProgram.provider_schema_id].definition
                                  .properties.categories.items.enum
                              "
                              :label="$bct('Select all that apply')"
                              variant="filled"
                              chips
                              closable-chips
                              hide-details
                              multiple
                            />
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_season.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_season.alias || 'Season',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-radio-group
                            v-model="filters.program_season"
                            class="mt-0"
                            hide-details
                          >
                            <v-radio
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.program_season.enum"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                            />
                          </v-radio-group>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .quality_rating_score.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Quality rating') }}
                        </v-col>
                      </v-row>

                      <v-row dense>
                        <v-col>
                          <div v-if="store.state.site.ratings.Description">
                            <div class="c-light-black fs-15 pt-2 pb-4">
                              <span
                                v-text="store.state.site.ratings.Description"
                                class="c-light-black fs-15"
                              />
                              <a
                                v-if="store.state.site.ratings.Link"
                                :href="store.state.site.ratings.Link"
                                class="c-primary fs-15 ms-1 underlined"
                                target="_blank"
                              >
                                {{ $bct('Learn more') }}
                              </a>
                            </div>
                          </div>
                          <v-radio-group
                            v-model="filters.minimum_quality_rating"
                            class="mt-0 mb-2"
                            hide-details
                          >
                            <div
                              v-for="(level, index) in store.state.site.ratings.text
                                .slice()
                                .reverse()"
                              :key="index"
                            >
                              <RadioWithContext
                                :index="index"
                                :item="level"
                                :suffix="
                                  store.state.site.ratings.text.length - index <
                                  store.state.site.ratings.text.length
                                    ? 'or higher'
                                    : null
                                "
                                :value="store.state.site.ratings.max - index"
                              />
                              <v-divider
                                v-if="level.subtitle"
                                v-show="index + 1 < store.state.site.ratings.text.length"
                                class="my-3"
                              />
                            </div>
                          </v-radio-group>
                          <v-btn
                            v-show="filters.minimum_quality_rating"
                            @click="filters.minimum_quality_rating = null"
                            color="primary"
                            size="small"
                          >
                            {{ $bct('Clear filter') }}
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .hours.filterable
                      "
                    >
                      <v-divider class="my-6" />
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Hours') }}
                        </v-col>
                      </v-row>
                      <v-row>
                        <LabeledOpenClose
                          v-model="hoursRange"
                          close-title="Pickup time"
                          open-title="Drop-off time"
                          title-class="fs-14"
                        />
                      </v-row>
                      <div class="c-gray fs-15 mt-2 mb-3 mxw-400">
                        {{
                          $bct(
                            'We’ll show you results that offer drop-off at the time requested or earlier, and pick-up at the time requested or later',
                          )
                        }}
                      </div>
                      <v-row dense>
                        <v-col
                          class="fw-600 fs-14"
                          cols="12"
                        >
                          {{ $bct('Days of the week') }}
                        </v-col>
                        <v-col cols="12">
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Monday')"
                            class="mt-0"
                            density="compact"
                            value="0"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Tuesday')"
                            class="mt-2"
                            density="compact"
                            value="1"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Wednesday')"
                            class="mt-2"
                            density="compact"
                            value="2"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Thursday')"
                            class="mt-2"
                            density="compact"
                            value="3"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Friday')"
                            class="mt-2"
                            density="compact"
                            value="4"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Saturday')"
                            class="mt-2"
                            density="compact"
                            value="5"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Sunday')"
                            class="mt-2"
                            density="compact"
                            value="6"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </div>

                    <SearchMoreFilters :value="filters" />
                  </v-card-text>
                  <v-divider />
                  <v-card-actions class="pt-2">
                    <v-btn
                      @click="clearMoreFilters()"
                      color="primary"
                      data-cy="clear-filters"
                      variant="text"
                    >
                      {{ $bct('Clear filters') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      @click="save()"
                      color="primary"
                      data-cy="apply-filter"
                    >
                      {{ $bct('Apply') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>

            <v-col
              class="ta-right"
              cols="12"
              sm="4"
            >
              <v-btn
                @click="listView = !listView"
                class="bg-white focus-very-visible"
                color="primary"
                data-cy="results-mode-button"
                variant="outlined"
              >
                <v-icon
                  v-show="!listView"
                  icon="west"
                  start
                />
                {{ $bct(!listView ? 'List view' : 'Map view') }}
                <v-icon
                  v-show="listView"
                  icon="east"
                  end
                />
              </v-btn>
            </v-col>
          </v-row>

          <v-row
            v-if="searchFlags.dobSearchEnabled"
            class="mb-3 mt-0"
          >
            <v-col>
              <span class="fs-16 mr-1">
                {{ $bct(`Search results show options beginning on`) }}
              </span>
              <v-btn
                @click="openCareNeededDateDialog = !openCareNeededDateDialog"
                :class="['bc-outlined-gray', 'bg-white', 'focus-visible']"
                :title="formattedCareNeededDate"
                rounded
              >
                {{ formattedCareNeededDate }}
              </v-btn>
            </v-col>

            <v-dialog
              v-model="openCareNeededDateDialog"
              max-width="330"
            >
              <v-card>
                <DatePicker
                  @update:model-value="changeCareNeededDate"
                  :allowed-dates="allowedDates"
                  :model-value="getLocalDate(careNeededDate)"
                  class="pa-2"
                  scrollable
                />

                <v-card-actions class="pa-4">
                  <v-row class="ma-0">
                    <v-col
                      class="d-flex justify-start pa-0"
                      cols="6"
                    >
                      <v-btn
                        @click="resetCareNeededDate"
                        :loading="localProcessing"
                        :ripple="false"
                        class="focus-very-visible"
                        color="primary"
                        size="large"
                        variant="outlined"
                      >
                        {{ $bct('Reset') }}
                      </v-btn>
                    </v-col>
                    <v-col
                      class="d-flex justify-end pa-0"
                      cols="6"
                    >
                      <v-btn
                        @click="applyCareNeededDate()"
                        :loading="localProcessing"
                        :ripple="false"
                        class="focus-very-visible"
                        color="primary"
                        size="large"
                      >
                        {{ $bct('Apply') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-row>
        </template>

        <template v-if="searched && searchTab == 1">
          <v-row class="d-flex align-center mb-1 mt-2">
            <v-col
              class="ta-right"
              cols="12"
              sm="12"
            >
              <v-btn
                @click="listView = !listView"
                class="bg-white focus-very-visible"
                color="primary"
                data-cy="results-mode-button"
                variant="outlined"
              >
                <v-icon
                  v-show="!listView"
                  icon="west"
                  start
                />
                {{ $bct(!listView ? 'List view' : 'Map view') }}
                <v-icon
                  v-show="listView"
                  icon="east"
                  end
                />
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <div v-show="!listView">
          <ProviderPreferenceMapResults
            @select="selectedMatch = $event"
            ref="map"
            :matches="matches"
            :search="search"
            :search-counter="searchCounter"
            :visible="!listView"
          />

          <template v-if="selectedMatch">
            <SimpleProviderCard
              @open="emit('open', selectedMatch.id)"
              @toggle="emit('toggle', [selectedMatch.id, $event])"
              :key="selectedMatch.id"
              :active="providerIds.includes(selectedMatch.id)"
              :provider="selectedMatch"
              :selectable="belowSelectionLimit"
            />
          </template>
        </div>

        <div v-show="listView">
          <SimpleProviderCard
            v-for="provider in matches"
            @open="emit('open', provider.id)"
            @toggle="emit('toggle', [provider.id, $event])"
            :key="provider.id"
            :active="providerIds.includes(provider.id)"
            :provider="provider"
            :selectable="belowSelectionLimit"
            class="my-4"
          />
        </div>

        <template v-if="matches.length > 0">
          <v-card
            class="bottom-0 elevation-0 bg-super-light-blue w-100pc pb-2 pt-1"
            tile
          >
            <v-pagination
              @update:model-value="changePage"
              :disabled="localProcessing"
              :length="pages"
              :model-value="page"
              :total-visible="8"
              rounded
            />
          </v-card>
        </template>
      </div>

      <vertical-spacer :min-height="50" />
    </div>

    <v-divider />

    <v-row class="my-4 px-4">
      <v-col class="d-flex justify-end">
        <v-btn
          @click="emit('next')"
          :disabled="providerIds == 0"
          append-icon="east"
          class="focus-very-visible"
          color="primary"
          size="x-large"
          tile
        >
          {{ $bct('Continue with selections') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import childrenSearchParam from '@/parent/services/children-search-param';
import DatePicker from '@/shared/components/DatePicker.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import LabeledVolume from '@/shared/components/form/LabeledVolume.vue';
import ProviderPreferenceMapResults from '@/parent/components/subsidy/ProviderPreferenceMapResults.vue';
import RadioWithContext from '@/shared/components/form/RadioWithContext.vue';
import SearchFlags from '@/parent/services/search-flags';
import SearchMoreFilters from '@/shared/components/search/MoreFilters.vue';
import SimpleProviderCard from '@/shared/components/SimpleProviderCard.vue';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';
import { capitalize } from '@/plugins/filters';
import { scrollTo } from 'vuetify/lib/composables/goto';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  childData: {
    type: Object,
    default: null,
  },
  defaultQuery: {
    type: String,
    default: null,
  },
  previousChildName: {
    type: String,
    default: null,
  },
  previousSubsidyProviderIds: {
    type: Array,
    default: null,
  },
  processing: Boolean,
  providerIds: {
    type: Array,
    default: null,
  },
  selectedProviders: {
    type: Array,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['copy', 'next', 'open', 'toggle']);

const { api } = useApi();
const eventBus = useEventBus();
const { locale } = useI18n();
const store = useStore();
const $bct = inject('translator');

const searchFlags = new SearchFlags(store);
const ageGroups = ref(searchFlags.ageGroups);
const careNeededDate = ref(
  props.subsidyProgram.search_for_care_as_of_date || new Date().toLocaleDateString('en-CA'),
);
const filters = ref(createDefaultSearch());
const hoursRange = ref([900, 1600]);
const listView = ref(true);
const localProcessing = ref(props.processing);
const matches = ref([]);
const moreFiltersDialogIsVisible = ref(null);
const openCareNeededDateDialog = ref(false);
const page = ref(1);
const pages = ref(1);
const search = ref(null);
const searchCounter = ref(0);
const searched = ref(false);
const searching = ref(false);
const searchQuery = ref(props.defaultQuery);
const searchQueryChanged = ref(!!props.defaultQuery);
const searchTab = ref(0);
const selectedMatch = ref(null);
const viewSelectedResults = ref(false);
const careNeededDateChanged = ref(false);

const belowSelectionLimit = computed(() => {
  if (props.subsidyProgram.selection_limit === 0) {
    return true;
  }
  return props.subsidyProgram.selection_limit > props.providerIds.length;
});

const formattedCareNeededDate = computed(() => {
  return new Date(careNeededDate.value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'utc',
  });
});

const moreFiltersActive = computed(() => {
  return (
    filters.value.categories.length > 0 ||
    Object.values(filters.value.highlights).some((item) => item.length > 0) ||
    Object.values(filters.value.custom).some((item) => item && item.length > 0) ||
    filters.value.minimum_quality_rating ||
    filters.value.payment_subsidies_accepted.length > 0 ||
    filters.value.program_season ||
    filters.value.program_types.length > 0 ||
    !!filters.value.subsidy
  );
});

const moreFiltersButtonClass = computed(() => {
  let c = ['me-2'];
  if (moreFiltersActive.value) {
    c = c.concat(['bg-white', 'bc-primary', 'b-1']);
  } else {
    c = c.concat(['bg-white', 'bc-very-light-gray']);
  }
  return c.join(' ');
});

watch(searchTab, () => {
  listView.value = true;
});

function createDefaultSearch() {
  return {
    categories: [],
    custom: {},
    dropoff: null,
    highlights: {},
    minimum_quality_rating: null,
    payment_subsidies_accepted: [],
    pickup: null,
    program_season: null,
    program_types: [],
    schema_id: props.subsidyProgram.provider_schema_id,
    vacancy: null,
    week_days: [],
  };
}

function allowedDates(date) {
  const inputDate = new Date(date);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return inputDate.getTime() >= currentDate.getTime();
}

function applyCareNeededDate() {
  if (careNeededDateChanged.value) {
    save();
    openCareNeededDateDialog.value = false;
  }
}

function changeCareNeededDate(value) {
  careNeededDate.value = value;
  searchQueryChanged.value = true;
  careNeededDateChanged.value = true;
}

async function changePage(newPage) {
  page.value = newPage;
  if (listView.value) {
    await scrollTo(0);
  }
  loadMatches();
}

function clearHours() {
  hoursRange.value = [900, 1600];
  filters.value.dropoff = null;
  filters.value.pickup = null;
  filters.value.week_days = [];
}

function clearMoreFilters() {
  clearHours();
  filters.value.categories = [];
  filters.value.custom = {};
  filters.value.highlights = {};
  filters.value.minimum_quality_rating = null;
  filters.value.payment_subsidies_accepted = [];
  filters.value.program_season = null;
  filters.value.program_types = [];
  moreFiltersDialogIsVisible.value = false;
  save();
}

function copyPrevious() {
  viewSelectedResults.value = true;
  emit('copy');
}

function getLocalDate(date) {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string') {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date();
}

function loadMatches() {
  api.match.index(
    search.value.id,
    {
      page: page.value,
      subsidy_program_id: props.subsidyProgram.id,
    },
    (response) => {
      searching.value = false;
      matches.value = response.data;
      pages.value = parseInt(response.headers['x-page-count'] || '0', 10);
      searchCounter.value += 1;
    },
  );
}

function modifyCount(index, addition) {
  ageGroups.value[index].count += addition;
}

function resetCareNeededDate() {
  if (careNeededDate.value === props.subsidyProgram.search_for_care_as_of_date) return;

  careNeededDate.value = props.subsidyProgram.search_for_care_as_of_date;
  searchQueryChanged.value = true;
}

function resetResults() {
  matches.value = [];
  localProcessing.value = false;
  searched.value = false;
  searchQuery.value = null;
}

function save() {
  searchProviders();
}

function searchByAddress() {
  if (filters.value.week_days.length > 0) {
    [filters.value.dropoff, filters.value.pickup] = hoursRange.value;
  } else {
    filters.value.dropoff = null;
    filters.value.pickup = null;
  }

  const children = childrenSearchParam({
    searchFlags,
    child: props.childData,
    careNeededDate: careNeededDate.value,
    toArray: true,
  });

  searched.value = true;
  api.search.create(
    {
      ...filters.value,
      children,
      categories: filters.value.categories,
      unformatted_origin: searchQuery.value,
      location_type: 'home',
      subsidy_program_id: props.subsidyProgram.id,
    },
    (resp) => {
      search.value = resp.data;
      page.value = 1;
      searchQueryChanged.value = false;
      loadMatches();
    },
    (err) => {
      eventBus.error(err);
      searching.value = false;
    },
  );
}

function searchByQuery() {
  searchCounter.value += 1;
  const counter = searchCounter.value;
  searched.value = true;
  api.public_api.provider.index(
    {
      include: 'details',
      query: searchQuery.value,
      schema_id: props.subsidyProgram.provider_schema_id,
      subsidy_program_id: props.subsidyProgram.id,
    },
    (response) => {
      if (counter === searchCounter.value) {
        matches.value = response.data;
        searching.value = false;
        searchQueryChanged.value = false;
        pages.value = 0;
      }
    },
  );
}

function searchProviders() {
  searching.value = true;
  moreFiltersDialogIsVisible.value = false;

  if (!searchQuery.value) {
    matches.value = [];
    searching.value = false;
    return false;
  }

  if (searchTab.value === 0) {
    return searchByAddress();
  }
  return searchByQuery();
}
</script>
